import { lazy } from 'react';
import { createBrowserRouter, useRouteError } from 'react-router-dom';
import { LazyLoadRoute, ProtectedRoute } from '@ui-framework/index';
import * as AppModule from '@modules/AppModule';
import Profile from '@product-framework/pages/profile';
import ErrorPage, { ERROR_PAGE_TYPE } from '../pages/ErrorPage';

const Login = LazyLoadRoute(lazy(() => import('../pages/Login')));
const Dashboard = LazyLoadRoute(lazy(() => import('../pages/Dashboard')));
const LegalPrivacy = LazyLoadRoute(
  lazy(() => import('../pages/Legal/Privacy')),
);
const LegalAcceptableUse = LazyLoadRoute(
  lazy(() => import('../pages/Legal/AcceptableUse')),
);
const LegalSecurity = LazyLoadRoute(
  lazy(() => import('../pages/Legal/Security')),
);
const LegalTerms = LazyLoadRoute(lazy(() => import('../pages/Legal/Terms')));

function BubbleRouteError() {
  const error = useRouteError();
  return <ErrorPage codeErrors={{ message: error?.message }} />;
}

const routes = [
  {
    path: '/',
    element: <Login />,
    errorElement: <BubbleRouteError />,
  },
  {
    path: '/unauthorized',
    element: <ErrorPage type={ERROR_PAGE_TYPE.FORBIDDEN} />,
    errorElement: <BubbleRouteError />,
  },
  {
    path: '/servicedown',
    element: <ErrorPage type={ERROR_PAGE_TYPE.SERVICE_DOWN} />,
    errorElement: <BubbleRouteError />,
  },
  {
    path: '/servererror',
    element: <ErrorPage type={ERROR_PAGE_TYPE.SERVER_ERROR} />,
    errorElement: <BubbleRouteError />,
  },
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute
        ErrorPage={ErrorPage}
        ERROR_PAGE_TYPE={ERROR_PAGE_TYPE}
        Component={Dashboard}
        componentProps={{
          AppModule,
        }}
      />
    ),
    errorElement: <BubbleRouteError />,
  },
  {
    path: '/profile',
    element: (
      <ProtectedRoute
        ErrorPage={ErrorPage}
        ERROR_PAGE_TYPE={ERROR_PAGE_TYPE}
        Component={Profile}
        componentProps={{
          AppModule,
        }}
      />
    ),
    errorElement: <BubbleRouteError />,
  },
  {
    path: '/legal/privacy',
    element: <LegalPrivacy />,
    errorElement: <BubbleRouteError />,
  },
  {
    path: '/legal/acceptable-use',
    element: <LegalAcceptableUse />,
    errorElement: <BubbleRouteError />,
  },
  {
    path: '/legal/security',
    element: <LegalSecurity />,
    errorElement: <BubbleRouteError />,
  },
  {
    path: '/legal/terms',
    element: <LegalTerms />,
    errorElement: <BubbleRouteError />,
  },
  {
    path: '*',
    element: <ErrorPage type={ERROR_PAGE_TYPE.NOT_FOUND} />,
    errorElement: <BubbleRouteError />,
  },
];

export const getRoutesFromModule = modules => {
  let tmpRoutes = [];
  modules.every(item => {
    if (item?.routes) {
      const moduleRoutes = item?.routes({
        ProtectedRoute,
        BubbleRouteError,
      });
      tmpRoutes = [...tmpRoutes, ...moduleRoutes];
    }
    return true;
  });
  return tmpRoutes;
};

const injectAdditionalRoutes = (existingRoutes, additionalRoutes = []) => {
  const updatedRouteInfo = existingRoutes;
  // Add other routes before catch all route
  const positionToAdd = routes.length - 1;
  if (additionalRoutes.length > 0) {
    updatedRouteInfo.splice(positionToAdd, 0, ...additionalRoutes);
  }

  return updatedRouteInfo;
};

const getRouter = additionalRoutes => {
  const allRoutes = injectAdditionalRoutes(routes, additionalRoutes);
  return createBrowserRouter(allRoutes, {
    basename: process.env.PUBLIC_URL,
  });
};

export default getRouter;
